import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';

const IN_APP_NOTIFS = [
  { key: 'transaction_alerts', label: 'Transaction Alerts', desc: 'New spend logged', icon: 'CreditCard' },
  { key: 'budget_warnings', label: 'Budget Limit Warnings', desc: 'Approaching category limit', icon: 'AlertTriangle' },
  { key: 'fraud_alerts', label: 'Fraud Alerts', desc: 'Suspicious activity detected', icon: 'Shield' },
  { key: 'partner_updates', label: 'Partner Updates', desc: 'New partner offers & deals', icon: 'Store' },
  { key: 'subscription_changes', label: 'Subscription Changes', desc: 'Plan updates & billing', icon: 'RefreshCw' },
  { key: 'goal_milestones', label: 'Goal Milestone Reached', desc: 'Progress on your goals', icon: 'Trophy' },
];

const EMAIL_NOTIFS = [
  { key: 'daily_digest', label: 'Daily Digest Email', desc: 'Daily spending summary', icon: 'Mail', hasTime: true },
  { key: 'weekly_summary', label: 'Weekly Spending Summary', desc: 'Weekly financial overview', icon: 'BarChart2' },
  { key: 'fraud_alert_emails', label: 'Fraud Alert Emails', desc: 'Immediate fraud notifications', icon: 'ShieldAlert' },
  { key: 'subscription_emails', label: 'Subscription Change Emails', desc: 'Billing & plan changes', icon: 'Receipt' },
];

const BUDGET_CATEGORIES = [
  { key: 'groceries', label: 'Groceries', icon: 'ShoppingCart' },
  { key: 'dining', label: 'Dining', icon: 'Utensils' },
  { key: 'shopping', label: 'Shopping', icon: 'ShoppingBag' },
  { key: 'entertainment', label: 'Entertainment', icon: 'Tv' },
  { key: 'transport', label: 'Transport', icon: 'Car' },
  { key: 'healthcare', label: 'Healthcare', icon: 'Heart' },
];

const DIGEST_TIMES = ['7:00 AM', '8:00 AM', '9:00 AM'];

const DEFAULT_PREFS = {
  transaction_alerts: true,
  budget_warnings: true,
  fraud_alerts: true,
  partner_updates: false,
  subscription_changes: true,
  goal_milestones: true,
  daily_digest: true,
  weekly_summary: true,
  fraud_alert_emails: true,
  subscription_emails: true,
  digest_time: '8:00 AM',
  budget_thresholds: {
    groceries: { enabled: true, threshold: 80 },
    dining: { enabled: true, threshold: 80 },
    shopping: { enabled: false, threshold: 80 },
    entertainment: { enabled: true, threshold: 80 },
    transport: { enabled: false, threshold: 80 },
    healthcare: { enabled: true, threshold: 80 },
  },
};

function ToggleSwitch({ checked, onChange }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className="relative w-11 h-6 rounded-full transition-all duration-300 flex-shrink-0"
      style={{ background: checked ? '#00E5FF' : 'rgba(255,255,255,0.1)', boxShadow: checked ? '0 0 10px rgba(0,229,255,0.4)' : 'none' }}
    >
      <span
        className="absolute top-0.5 w-5 h-5 rounded-full transition-all duration-300"
        style={{ background: checked ? '#0D1B2A' : '#475569', left: checked ? 'calc(100% - 22px)' : '2px' }}
      />
    </button>
  );
}

export default function NotificationsTab({ onToast }) {
  const { userProfile, updateProfile } = useAuth();
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (userProfile?.notification_preferences) {
      setPrefs(prev => ({ ...DEFAULT_PREFS, ...userProfile?.notification_preferences }));
    }
  }, [userProfile]);

  const savePrefs = async (newPrefs) => {
    setSaving(true);
    try {
      const { error } = await updateProfile({ notification_preferences: newPrefs });
      if (error) throw error;
      onToast?.({ type: 'success', message: 'Preferences saved!' });
    } catch (err) {
      onToast?.({ type: 'error', message: err?.message || 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (key, value) => {
    const newPrefs = { ...prefs, [key]: value };
    setPrefs(newPrefs);
    savePrefs(newPrefs);
  };

  const handleThreshold = (cat, field, value) => {
    const newPrefs = {
      ...prefs,
      budget_thresholds: {
        ...prefs?.budget_thresholds,
        [cat]: { ...(prefs?.budget_thresholds?.[cat] || { enabled: true, threshold: 80 }), [field]: value },
      },
    };
    setPrefs(newPrefs);
  };

  const handleThresholdSave = () => savePrefs(prefs);

  return (
    <div className="space-y-8">
      {saving && (
        <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg" style={{ background: 'rgba(0,229,255,0.08)', color: '#00E5FF' }}>
          <div className="w-3 h-3 border border-[#00E5FF] border-t-transparent rounded-full animate-spin" />
          Saving preferences...
        </div>
      )}
      {/* In-App Notifications */}
      <div>
        <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
          <Icon name="Bell" size={16} color="#00E5FF" />
          In-App Notifications
        </h3>
        <div className="space-y-3">
          {IN_APP_NOTIFS?.map(({ key, label, desc, icon }) => (
            <div key={key} className="flex items-center justify-between p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(0,229,255,0.1)' }}>
                  <Icon name={icon} size={15} color="#00E5FF" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">{label}</p>
                  <p className="text-xs" style={{ color: '#64748B' }}>{desc}</p>
                </div>
              </div>
              <ToggleSwitch checked={!!prefs?.[key]} onChange={val => handleToggle(key, val)} />
            </div>
          ))}
        </div>
      </div>
      {/* Email Notifications */}
      <div>
        <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
          <Icon name="Mail" size={16} color="#7B2FBE" />
          Email Notifications
        </h3>
        <div className="space-y-3">
          {EMAIL_NOTIFS?.map(({ key, label, desc, icon, hasTime }) => (
            <div key={key} className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(123,47,190,0.15)' }}>
                    <Icon name={icon} size={15} color="#C084FC" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{label}</p>
                    <p className="text-xs" style={{ color: '#64748B' }}>{desc}</p>
                  </div>
                </div>
                <ToggleSwitch checked={!!prefs?.[key]} onChange={val => handleToggle(key, val)} />
              </div>
              {hasTime && prefs?.[key] && (
                <div className="mt-3 ml-12">
                  <label className="text-xs mb-1 block" style={{ color: '#64748B' }}>Send time</label>
                  <select
                    value={prefs?.digest_time || '8:00 AM'}
                    onChange={e => handleToggle('digest_time', e?.target?.value)}
                    className="px-3 py-1.5 rounded-lg text-xs text-white outline-none"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(0,229,255,0.15)' }}
                  >
                    {DIGEST_TIMES?.map(t => <option key={t} value={t} style={{ background: '#0D1B2A' }}>{t}</option>)}
                  </select>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Budget Alert Thresholds */}
      <div>
        <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
          <Icon name="SlidersHorizontal" size={16} color="#F59E0B" />
          Budget Alert Thresholds
        </h3>
        <div className="space-y-4">
          {BUDGET_CATEGORIES?.map(({ key, label, icon }) => {
            const catPrefs = prefs?.budget_thresholds?.[key] || { enabled: true, threshold: 80 };
            return (
              <div key={key} className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Icon name={icon} size={14} color="#F59E0B" />
                    <span className="text-sm font-medium text-white">{label}</span>
                  </div>
                  <ToggleSwitch checked={!!catPrefs?.enabled} onChange={val => handleThreshold(key, 'enabled', val)} />
                </div>
                {catPrefs?.enabled && (
                  <div>
                    <p className="text-xs mb-2" style={{ color: '#64748B' }}>
                      Alert me when I've used <span className="font-semibold" style={{ color: '#F59E0B' }}>{catPrefs?.threshold || 80}%</span> of my {label} budget
                    </p>
                    <input
                      type="range" min="50" max="100" step="5"
                      value={catPrefs?.threshold || 80}
                      onChange={e => handleThreshold(key, 'threshold', parseInt(e?.target?.value))}
                      onMouseUp={handleThresholdSave}
                      onTouchEnd={handleThresholdSave}
                      className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                      style={{ accentColor: '#F59E0B' }}
                    />
                    <div className="flex justify-between mt-1">
                      <span className="text-xs" style={{ color: '#475569' }}>50%</span>
                      <span className="text-xs" style={{ color: '#475569' }}>100%</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
